import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { 
  Assignment as AssignmentIcon, 
  Person as PersonIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TemplatePreview } from "../../components/TemplatePreview";
import PatientSelect from "../../components/PatientSelect";
import { useTitle } from "../../hooks/useTitle";

import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.default,
  },
  section: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: "1px solid " + theme.palette.divider,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    }
  },
  patientSelectorContainer: {
    display: "flex",
    alignItems: "center",
    maxWidth: 600,
  },
  templateCard: {
    position: "relative",
    height: "240px",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[4]
    }
  },
  templateCardPreview: {
    flex: 1,
    overflow: "hidden",
    padding: 0,
    backgroundColor: "#f5f5f5", 
  },
  overlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(76, 175, 80, 0.2)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 4,
    border: "3px solid #4caf50",
    pointerEvents: "none"
  },
  templateHeader: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    color: "#fff",
    padding: "4px 8px",
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  starButton: {
    color: "#FFD700",
    padding: 4,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  }
}));

const Prescriptions = () => {
  useTitle("Crear Receta Médica");
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const [selectedPatientEntry, setSelectedPatientEntry] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [favoriteTemplateId, setFavoriteTemplateId] = useState(null);
  const [findingPatient, setFindingPatient] = useState(false);

  const isDoctor = user && user.isDoctor;

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const patientNameParam = queryParams.get("patientName");
    const contactIdParam = queryParams.get("contactId");

    if (selectedPatientEntry) return;

    if ((patientNameParam || contactIdParam) && isDoctor) {
      const findPatient = async () => {
        setFindingPatient(true);
        try {
          const params = {};
          if (patientNameParam) params.searchParam = patientNameParam;
          
          const { data } = await api.get("/doctors/" + user.id + "/patients", { params });
          const patientsList = Array.isArray(data) ? data : (data.patients || []);

          let found = null;

          if (contactIdParam) {
            const contactMatches = patientsList.filter(p => String(p.contact.id) === String(contactIdParam));
            if (contactMatches.length > 0) {
              if (patientNameParam) {
                found = contactMatches.find(p => p.patientName === patientNameParam);
                if (!found) found = contactMatches[0];
              } else {
                found = contactMatches[0];
              }
            }
          } 
          
          if (!found && patientNameParam) {
            found = patientsList.find(p => p.patientName === patientNameParam);
          }

          if (found) {
            setSelectedPatientEntry(found);
          }
        } catch (err) {
          console.error("Error buscando paciente automático:", err);
        } finally {
          setFindingPatient(false);
        }
      };
      findPatient();
    }
  }, [location.search, isDoctor, user, selectedPatientEntry]);

  useEffect(() => {
    if (selectedPatientEntry) {
      setLoadingTemplates(true);
      
      const fetchInitialData = async () => {
        try {
          const [templatesRes, favoriteRes] = await Promise.all([
              api.get("/prescriptionTemplates?includeOtherDoctorsTemplates=false"), 
              api.get("/favorite-template")
          ]);

          const fetchedTemplates = templatesRes.data;
          const favorite = favoriteRes.data;

          setTemplates(fetchedTemplates);

          if (favorite && favorite.templateId) {
            setFavoriteTemplateId(favorite.templateId);
            const fav = fetchedTemplates.find(t => t.id === favorite.templateId);
            if (fav && !selectedTemplate) {
               setSelectedTemplate(fav);
            }
          }

        } catch (err) {
          toastError(err);
        } finally {
          setLoadingTemplates(false);
        }
      };
      fetchInitialData();
    } else {
      setTemplates([]);
    }
  }, [selectedPatientEntry]);

  const handleSetFavorite = async (e, templateId) => {
    e.stopPropagation(); // Evitar seleccionar la carta
    try {
      await api.post("/favorite-template", { templateId });
      setFavoriteTemplateId(templateId);
      toast.success("Plantilla marcada como favorita");
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreatePrescription = async () => {
    if (selectedPatientEntry && selectedTemplate) {
      
      if (!favoriteTemplateId) {
        try {
          await api.post("/favorite-template", { 
            templateId: selectedTemplate.id 
          });
        } catch (err) {
          console.error("No se pudo guardar la plantilla favorita:", err);
        }
      }
      
      history.push({
        pathname: "/Prescriptions/Editor/" + selectedPatientEntry.contact.id + "/" + selectedTemplate.id,
        state: {
          patientName: selectedPatientEntry.patientName 
        }
      });
    }
  };

  if (!isDoctor) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Crear Receta</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} variant="outlined">
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="50vh">
             <AssignmentIcon style={{ fontSize: 60, opacity: 0.2, marginBottom: 16 }} />
             <Typography variant="h6" color="textSecondary">
                Acceso Restringido
             </Typography>
             <Typography variant="body1" color="textSecondary">
                Esta sección solo está disponible para usuarios con perfil médico.
             </Typography>
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Crear Receta Médica</Title>
      </MainHeader>
      
      <div className={classes.mainPaper}>
        <div className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            <PersonIcon /> Paso 1: Seleccionar Paciente
          </Typography>
          
          <div className={classes.patientSelectorContainer}>
            <Box flexGrow={1}>
              {findingPatient ? (
                 <Box display="flex" alignItems="center">
                    <CircularProgress size={20} style={{ marginRight: 10 }} />
                    <Typography>Buscando paciente...</Typography>
                 </Box>
              ) : (
                <PatientSelect
                  value={selectedPatientEntry}
                  onChange={(entry) => setSelectedPatientEntry(entry)}
                  endpoint={"/doctors/" + user.id + "/patients"}
                />
              )}
            </Box>
          </div>
        </div>

        {selectedPatientEntry && (
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <AssignmentIcon /> Paso 2: Seleccionar Plantilla
            </Typography>
            
            <Box mb={3}>
              <Alert severity="info" variant="outlined" style={{ border: "1px dashed #2196f3", backgroundColor: "rgba(33, 150, 243, 0.05)" }}>
                <strong>Tip:</strong> Puedes crear recetas rápidas desde el <strong>Calendario</strong> haciendo clic en una cita finalizada. 
                <br />
                La plantilla marcada con la <strong>estrella dorada (<StarIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', color: '#FFD700' }} />)</strong> será la que se use automáticamente en ese acceso rápido.
              </Alert>
            </Box>
          
            {loadingTemplates ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {templates.map((template) => {
                  const isSelected = selectedTemplate?.id === template.id;
                  const isFavorite = favoriteTemplateId === template.id;

                  return (
                    <Grid item xs={12} sm={6} md={3} key={template.id}>
                      <Card className={classes.templateCard} variant="outlined">
                        
                        <div className={classes.templateHeader}>
                            <Typography variant="subtitle2" noWrap style={{ fontWeight: 600, flex: 1 }}>
                              {template.name}
                            </Typography>
                            <Tooltip title={isFavorite ? "Es tu favorita" : "Marcar como favorita"}>
                                <IconButton 
                                    className={classes.starButton}
                                    size="small"
                                    onClick={(e) => handleSetFavorite(e, template.id)}
                                >
                                    {isFavorite ? <StarIcon /> : <StarBorderIcon />}
                                </IconButton>
                            </Tooltip>
                        </div>

                        <CardActionArea
                          onClick={() => setSelectedTemplate(template)}
                          style={{ height: "100%", paddingTop: 30 }}
                        >
                          <CardContent className={classes.templateCardPreview}>
                            <TemplatePreview content={template.content} />
                          </CardContent>

                          {isSelected && (
                            <div className={classes.overlay}>
                              <AssignmentIcon style={{ fontSize: 64, color: "#2e7d32" }} />
                            </div>
                          )}
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </div>
        )}
        
        {selectedPatientEntry && selectedTemplate && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
             <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AssignmentIcon />}
                onClick={handleCreatePrescription}
                style={{ padding: "12px 24px", fontSize: "1rem" }}
              >
                Crear Receta usando "{selectedTemplate.name}""
              </Button>
          </Box>
        )}
      </div>
    </MainContainer>
  );
};

export default Prescriptions;